import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import {
  Search, Filter, ChevronDown, CheckSquare, Square, ArrowUpDown,
  UserCheck, UserX, Mail, GitCompare, RefreshCw, X, AlertTriangle,
  ChevronRight, Shield, Zap, Eye
} from "lucide-react";

const SCORE_COLOR = (s) => s >= 80 ? "text-emerald-400" : s >= 50 ? "text-amber-400" : "text-red-400";
const SCORE_BG = (s) => s >= 80 ? "bg-emerald-500" : s >= 50 ? "bg-amber-500" : "bg-red-500";
const SCORE_ROW_BG = (s) => s >= 80 ? "border-l-emerald-500" : s >= 50 ? "border-l-amber-500" : "border-l-red-400";
const CONF_COLOR = { High: "text-emerald-400", Medium: "text-amber-400", Low: "text-red-400" };
const STATUS_COLORS = {
  Pending: "bg-gray-500/20 text-gray-400",
  Shortlisted: "bg-emerald-500/20 text-emerald-400",
  Invited: "bg-blue-500/20 text-blue-400",
  Rejected: "bg-red-500/20 text-red-400"
};

const ShortlistDashboard = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("match_score");
  const [sortDir, setSortDir] = useState("desc");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterRec, setFilterRec] = useState("");
  const [filterMinScore, setFilterMinScore] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [editScore, setEditScore] = useState(null);

  useEffect(() => { fetchData(); }, [jobId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/shortlist/job/${jobId}`, { credentials: "include" });
      if (res.ok) setData(await res.json());
      else toast.error("Failed to load shortlist data");
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  const candidates = useMemo(() => {
    if (!data?.candidates) return [];
    let list = [...data.candidates];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c => c.candidate_name?.toLowerCase().includes(q) || c.ai_summary?.toLowerCase().includes(q));
    }
    if (filterStatus) list = list.filter(c => c.status === filterStatus);
    if (filterRec) list = list.filter(c => c.recommendation === filterRec);
    if (filterMinScore > 0) list = list.filter(c => (c.recruiter_override_score ?? c.match_score) >= filterMinScore);
    list.sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (sortBy === "match_score") { va = a.recruiter_override_score ?? a.match_score; vb = b.recruiter_override_score ?? b.match_score; }
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    });
    return list;
  }, [data, search, filterStatus, filterRec, filterMinScore, sortBy, sortDir]);

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll80 = () => { const ids = candidates.filter(c => (c.recruiter_override_score ?? c.match_score) >= 80).map(c => c.shortlist_id); setSelected(new Set(ids)); };
  const toggleSort = (col) => { if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(col); setSortDir("desc"); } };

  const bulkAction = async (action) => {
    const ids = [...selected];
    if (!ids.length) { toast.error("No candidates selected"); return; }
    const res = await fetch(`${API}/shortlist/bulk-action`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ action, shortlist_ids: ids }) });
    if (res.ok) { toast.success(`${action} applied to ${ids.length} candidates`); setSelected(new Set()); fetchData(); }
    else toast.error("Action failed");
  };

  const updateStatus = async (id, status) => {
    const res = await fetch(`${API}/shortlist/candidate/${id}/status`, { method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ status }) });
    if (res.ok) { toast.success("Status updated"); fetchData(); }
  };

  const saveOverrideScore = async (id, score) => {
    const res = await fetch(`${API}/shortlist/candidate/${id}/score`, { method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ score: parseInt(score) }) });
    if (res.ok) { toast.success("Score overridden"); setEditScore(null); fetchData(); }
  };

  const doCompare = async () => {
    const ids = [...selected];
    if (ids.length < 2 || ids.length > 3) { toast.error("Select 2 or 3 candidates"); return; }
    const res = await fetch(`${API}/shortlist/compare`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ shortlist_ids: ids }) });
    if (res.ok) { setCompareData(await res.json()); setCompareMode(true); }
  };

  if (loading) return (
    <div className="min-h-screen bg-obsidian"><Sidebar /><main className="lg:ml-64 p-6 pt-20 lg:pt-8 flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" /></main></div>
  );

  return (
    <div className="min-h-screen bg-obsidian" data-testid="shortlist-dashboard">
      <Sidebar />
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">AI <span className="text-crimson">SHORTLIST</span></h1>
            <p className="text-gray-400 text-sm mt-1">{data?.job?.title} — {data?.total || 0} candidates analyzed</p>
          </div>
          <Button onClick={fetchData} variant="outline" className="border-[#333] text-white text-xs"><RefreshCw className="w-3 h-3 mr-1" /> Refresh</Button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          <div className="col-span-2 sm:col-span-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-3 py-2 bg-obsidian-light border border-[#333] text-white text-sm" data-testid="search-candidates" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 bg-obsidian-light border border-[#333] text-white text-sm" data-testid="filter-status">
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Shortlisted">Shortlisted</option>
            <option value="Invited">Invited</option>
            <option value="Rejected">Rejected</option>
          </select>
          <select value={filterRec} onChange={e => setFilterRec(e.target.value)} className="px-3 py-2 bg-obsidian-light border border-[#333] text-white text-sm" data-testid="filter-recommendation">
            <option value="">All Matches</option>
            <option value="Top Match">Top Match</option>
            <option value="Strong Potential">Strong Potential</option>
            <option value="Weak Match">Weak Match</option>
            <option value="Not Suitable">Not Suitable</option>
          </select>
          <select value={filterMinScore} onChange={e => setFilterMinScore(Number(e.target.value))} className="px-3 py-2 bg-obsidian-light border border-[#333] text-white text-sm">
            <option value={0}>Min Score: Any</option>
            <option value={80}>80%+</option>
            <option value={70}>70%+</option>
            <option value={55}>55%+</option>
          </select>
          <Button onClick={selectAll80} variant="outline" className="border-[#333] text-white text-xs" data-testid="select-all-80">
            <CheckSquare className="w-3 h-3 mr-1" /> All 80%+
          </Button>
        </div>

        {/* Bulk Actions */}
        {selected.size > 0 && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-crimson/10 border border-crimson/20">
            <span className="text-sm text-white font-medium mr-2">{selected.size} selected</span>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => bulkAction("shortlist")} data-testid="bulk-shortlist"><UserCheck className="w-3 h-3 mr-1" /> Shortlist</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs" onClick={() => bulkAction("invite")} data-testid="bulk-invite"><Mail className="w-3 h-3 mr-1" /> Send Invite</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-xs" onClick={() => bulkAction("reject")} data-testid="bulk-reject"><UserX className="w-3 h-3 mr-1" /> Reject</Button>
            <Button size="sm" variant="outline" className="border-[#333] text-white text-xs" onClick={doCompare} data-testid="compare-btn"><GitCompare className="w-3 h-3 mr-1" /> Compare</Button>
            <Button size="sm" variant="ghost" className="text-gray-400 text-xs ml-auto" onClick={() => setSelected(new Set())}><X className="w-3 h-3 mr-1" /> Clear</Button>
          </motion.div>
        )}

        {/* Table */}
        {candidates.length > 0 ? (
          <div className="bg-obsidian-light border border-[#222] overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-[#333]">
                  <th className="p-3 w-8"><button onClick={() => { selected.size === candidates.length ? setSelected(new Set()) : setSelected(new Set(candidates.map(c => c.shortlist_id))); }}>{selected.size === candidates.length ? <CheckSquare className="w-4 h-4 text-crimson" /> : <Square className="w-4 h-4 text-gray-500" />}</button></th>
                  {[["candidate_name","Candidate"],["match_score","Score"],["confidence","Confidence"],["recommendation","Match"],["status","Status"],["shortlist_suggested","Shortlist?"]].map(([key, label]) => (
                    <th key={key} className="p-3 text-left text-xs text-gray-400 uppercase cursor-pointer hover:text-white" onClick={() => toggleSort(key)}>
                      <span className="flex items-center gap-1">{label}<ArrowUpDown className="w-3 h-3" /></span>
                    </th>
                  ))}
                  <th className="p-3 text-left text-xs text-gray-400 uppercase">Action</th>
                  <th className="p-3 w-8" />
                </tr>
              </thead>
              <tbody>
                {candidates.map(c => {
                  const score = c.recruiter_override_score ?? c.match_score;
                  const isExpanded = expanded === c.shortlist_id;
                  return (
                    <motion.tr key={c.shortlist_id} layout className={`border-b border-[#1a1a1a] border-l-4 ${SCORE_ROW_BG(score)} hover:bg-white/5 cursor-pointer`} data-testid={`candidate-row-${c.shortlist_id}`}>
                      <td className="p-3" onClick={e => e.stopPropagation()}>
                        <button onClick={() => toggleSelect(c.shortlist_id)}>{selected.has(c.shortlist_id) ? <CheckSquare className="w-4 h-4 text-crimson" /> : <Square className="w-4 h-4 text-gray-500" />}</button>
                      </td>
                      <td className="p-3" onClick={() => setExpanded(isExpanded ? null : c.shortlist_id)}>
                        <div className="text-sm text-white font-medium">{c.candidate_name}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[200px]">{c.candidate_email}</div>
                      </td>
                      <td className="p-3" onClick={e => e.stopPropagation()}>
                        {editScore === c.shortlist_id ? (
                          <input type="number" min="0" max="100" defaultValue={score} autoFocus className="w-16 px-2 py-1 bg-obsidian border border-crimson text-white text-sm" onBlur={e => saveOverrideScore(c.shortlist_id, e.target.value)} onKeyDown={e => e.key === "Enter" && saveOverrideScore(c.shortlist_id, e.target.value)} />
                        ) : (
                          <button onClick={() => setEditScore(c.shortlist_id)} className={`text-lg font-bold ${SCORE_COLOR(score)}`} title="Click to override">
                            {score}%
                            {c.recruiter_override_score !== null && c.recruiter_override_score !== undefined && <span className="text-[10px] text-gray-500 ml-1">(edited)</span>}
                          </button>
                        )}
                        <div className="w-16 h-1 bg-[#1a1a1a] mt-1"><div className={`h-full ${SCORE_BG(score)}`} style={{ width: `${score}%` }} /></div>
                      </td>
                      <td className="p-3" onClick={() => setExpanded(isExpanded ? null : c.shortlist_id)}><span className={`text-xs font-bold ${CONF_COLOR[c.confidence] || "text-gray-400"}`}>{c.confidence}</span></td>
                      <td className="p-3" onClick={() => setExpanded(isExpanded ? null : c.shortlist_id)}><span className={`text-xs px-2 py-0.5 ${score >= 85 ? "bg-emerald-500/20 text-emerald-400" : score >= 70 ? "bg-blue-500/20 text-blue-400" : score >= 55 ? "bg-amber-500/20 text-amber-400" : "bg-red-500/20 text-red-400"}`}>{c.recommendation}</span></td>
                      <td className="p-3" onClick={e => e.stopPropagation()}>
                        <select value={c.status} onChange={e => updateStatus(c.shortlist_id, e.target.value)} className={`text-xs px-2 py-1 border-0 ${STATUS_COLORS[c.status] || "bg-gray-500/20 text-gray-400"}`}>
                          <option value="Pending">Pending</option>
                          <option value="Shortlisted">Shortlisted</option>
                          <option value="Invited">Invited</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </td>
                      <td className="p-3" onClick={() => setExpanded(isExpanded ? null : c.shortlist_id)}><span className={`text-xs font-bold ${c.shortlist_suggested ? "text-emerald-400" : "text-red-400"}`}>{c.shortlist_suggested ? "Yes" : "No"}</span></td>
                      <td className="p-3 text-xs text-gray-400" onClick={() => setExpanded(isExpanded ? null : c.shortlist_id)}>{c.suggested_action}</td>
                      <td className="p-3"><ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? "rotate-90" : ""}`} /></td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>

            {/* Expanded Details */}
            <AnimatePresence>
              {expanded && (() => {
                const c = candidates.find(x => x.shortlist_id === expanded);
                if (!c) return null;
                return (
                  <motion.div key="detail" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="border-t border-crimson/30 bg-[#0d0d0d] p-6" data-testid="candidate-detail">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">AI Summary</h4>
                        <p className="text-sm text-gray-300 leading-relaxed">{c.ai_summary}</p>
                      </div>
                      <div>
                        <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Key Strengths</h4>
                        <ul className="space-y-1">{(c.key_strengths || []).map((s, i) => <li key={i} className="text-sm text-emerald-400 flex items-start gap-1"><span className="text-emerald-600 mt-0.5">+</span> {s}</li>)}</ul>
                        <h4 className="text-xs text-gray-500 uppercase tracking-wider mt-4 mb-2">Gaps / Concerns</h4>
                        <ul className="space-y-1">{(c.gaps_concerns || []).map((g, i) => <li key={i} className="text-sm text-amber-400 flex items-start gap-1"><span className="text-amber-600 mt-0.5">-</span> {g}</li>)}</ul>
                      </div>
                      <div>
                        <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Disqualifiers</h4>
                        {c.disqualifiers?.length > 0 ? (
                          <ul className="space-y-1">{c.disqualifiers.map((d, i) => <li key={i} className="text-sm text-red-400 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {d}</li>)}</ul>
                        ) : (
                          <p className="text-sm text-gray-500">None identified</p>
                        )}
                        {c.score_breakdown && Object.keys(c.score_breakdown).length > 0 && (
                          <>
                            <h4 className="text-xs text-gray-500 uppercase tracking-wider mt-4 mb-2">Score Breakdown</h4>
                            <div className="space-y-1">
                              {Object.entries(c.score_breakdown).map(([k, v]) => (
                                <div key={k} className="flex items-center justify-between text-xs">
                                  <span className="text-gray-400">{k.replace(/_/g, " ")}</span>
                                  <span className="text-white font-bold">{v}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })()}
            </AnimatePresence>
          </div>
        ) : (
          <div className="bg-obsidian-light border border-[#222] p-12 text-center">
            <Zap className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No candidates analyzed yet. Candidates will appear here automatically as they apply.</p>
          </div>
        )}
      </main>

      {/* Compare Modal */}
      <AnimatePresence>
        {compareMode && compareData && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setCompareMode(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-[#111] border border-[#222] w-full max-w-4xl max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="compare-modal">
              <div className="p-4 border-b border-[#222] flex items-center justify-between sticky top-0 bg-[#111] z-10">
                <h3 className="font-heading text-lg font-bold">CANDIDATE <span className="text-crimson">COMPARISON</span></h3>
                <button onClick={() => setCompareMode(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-4">
                <div className={`grid gap-4 ${compareData.candidates.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
                  {compareData.candidates.map(c => {
                    const score = c.recruiter_override_score ?? c.match_score;
                    return (
                      <div key={c.shortlist_id} className={`border-t-4 ${score >= 80 ? "border-t-emerald-500" : score >= 50 ? "border-t-amber-500" : "border-t-red-500"} bg-obsidian-light p-4`}>
                        <h4 className="text-white font-bold mb-1">{c.candidate_name}</h4>
                        <div className={`text-2xl font-bold ${SCORE_COLOR(score)} mb-2`}>{score}%</div>
                        <div className="text-xs text-gray-500 mb-3">Confidence: <span className={CONF_COLOR[c.confidence]}>{c.confidence}</span></div>
                        <div className="mb-3">
                          <div className="text-xs text-gray-500 uppercase mb-1">Strengths</div>
                          {(c.key_strengths || []).map((s, i) => <div key={i} className="text-xs text-emerald-400">+ {s}</div>)}
                        </div>
                        <div className="mb-3">
                          <div className="text-xs text-gray-500 uppercase mb-1">Gaps</div>
                          {(c.gaps_concerns || []).map((g, i) => <div key={i} className="text-xs text-amber-400">- {g}</div>)}
                        </div>
                        <div className="mb-3">
                          <div className="text-xs text-gray-500 uppercase mb-1">Disqualifiers</div>
                          {c.disqualifiers?.length > 0 ? c.disqualifiers.map((d, i) => <div key={i} className="text-xs text-red-400">{d}</div>) : <div className="text-xs text-gray-500">None</div>}
                        </div>
                        <div className="text-xs text-gray-400 mt-2 pt-2 border-t border-[#222]">Action: {c.suggested_action}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShortlistDashboard;
